import { filter, includes } from "lodash";

/**
 * Normalizes locations from either the old format (string) or new format (array)
 * @param {string|string[]|undefined} location - Single location string (legacy)
 * @param {string[]|undefined} locations - Array of locations (new format)
 * @param {string[]} defaultLocations - Default locations if neither is provided
 * @returns {string[]} Array of normalized locations
 */
export const normalizeLocations = (
  location,
  locations,
  defaultLocations = ["apps"]
) => {
  if (Array.isArray(locations) && locations.length > 0) {
    return locations;
  }
  if (location) {
    return [location];
  }
  return defaultLocations;
};

/**
 * Checks if a link should be visible to a user based on their role
 * @param {string[]|undefined} linkRoles - Roles allowed to see the link
 * @param {string} userRole - Current user's role
 * @returns {boolean} True if link should be visible
 */
export const isLinkVisibleToRole = (linkRoles, userRole) => {
  // If no roles specified, show to all (backward compatibility)
  if (!linkRoles || linkRoles.length === 0) {
    return true;
  }
  // API returns lowercase roles; useUserRole returns uppercase - compare case-insensitively
  const normalizedUserRole = userRole?.toLowerCase?.();
  const normalizedLinkRoles = linkRoles.map((r) => r?.toLowerCase?.());
  return includes(normalizedLinkRoles, normalizedUserRole);
};

/**
 * Filters external contents by location and user role
 * @param {Array} externalContents - Array of external content items
 * @param {string|string[]} targetLocations - Location(s) to filter for
 * @param {string} userRole - Current user's role
 * @returns {Array} Filtered array of links
 */
export const filterLinksByLocationAndRole = (
  externalContents,
  targetLocations,
  userRole
) => {
  if (!externalContents) {
    return [];
  }

  const targetLocationsArray = Array.isArray(targetLocations)
    ? targetLocations
    : [targetLocations];

  return filter(externalContents, (content) => {
    if (content.type !== "link") {
      return false;
    }

    const locations = normalizeLocations(content.location, content.locations, [
      "apps",
    ]);

    const shouldShow = targetLocationsArray.some((targetLoc) =>
      includes(locations, targetLoc)
    );

    if (!shouldShow) {
      return false;
    }

    return isLinkVisibleToRole(content.roles, userRole);
  });
};
